import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { usePublicPortalAppearance } from '../../context/PublicPortalAppearanceContext';
import PortalTemplatePickerGrid from './PortalTemplatePickerGrid';
import SharedTemplateChooser from './SharedTemplateChooser';
import { LOGIN_PORTAL_THEMES } from '../../theme/loginPortalThemes';

function SelectorSection({ icon, title, subtitle, themeId, onSelect, defaultExpanded = false, extra }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px !important',
        mb: 1.5,
        '&:before': { display: 'none' },
        overflow: 'hidden',
        bgcolor: 'background.paper'
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, minHeight: 52 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 0.5, minWidth: 0 }}>
          <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>{icon}</Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
              {subtitle}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={LOGIN_PORTAL_THEMES[themeId]?.label || themeId}
            sx={{ fontWeight: 600, height: 22, flexShrink: 0 }}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
        <PortalTemplatePickerGrid selectedId={themeId} onSelect={onSelect} showDetails columns="auto" />
        {extra}
      </AccordionDetails>
    </Accordion>
  );
}

/** Left-edge template chooser for the Browsing Page (shared atmosphere + portal extras). */
export default function PortalTemplateSelector() {
  const {
    layoutMode,
    setLayoutMode,
    templateId,
    contentTemplateId,
    setContentTemplateId,
    splitBrowseTemplateId,
    splitPreviewTemplateId,
    setSplitBrowseTemplateId,
    setSplitPreviewTemplateId,
    clearSplitBrowseTemplateOverride,
    clearSplitPreviewTemplateOverride,
    splitBrowseHasOverride,
    splitPreviewHasOverride
  } = usePublicPortalAppearance();

  return (
    <SharedTemplateChooser
      title="Browsing page look"
      subtitle="Page atmosphere is shared with Sign In, Register, and Landing."
      topOffset={{ xs: 68, sm: 76 }}
    >
      <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>
        Page layout
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={layoutMode}
        onChange={(_, v) => v && setLayoutMode(v)}
        sx={{ mb: 2, mt: 0.75 }}
      >
        <ToggleButton value="regular" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
          <ViewStreamIcon sx={{ fontSize: 16, mr: 0.5 }} />
          Straight
        </ToggleButton>
        <ToggleButton value="split" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
          <ViewColumnIcon sx={{ fontSize: 16, mr: 0.5 }} />
          Split
        </ToggleButton>
      </ToggleButtonGroup>

      <SelectorSection
        icon={<ViewQuiltIcon fontSize="small" />}
        title="Cards & content"
        subtitle="Jobs, trainings, exams"
        themeId={contentTemplateId}
        onSelect={setContentTemplateId}
        extra={
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
            <Button size="small" variant="text" onClick={() => setContentTemplateId(templateId)}>
              Match page atmosphere
            </Button>
          </Stack>
        }
      />
      {layoutMode === 'split' ? (
        <>
          <SelectorSection
            icon={<ViewColumnIcon fontSize="small" />}
            title="Split — left (browse)"
            subtitle="List column"
            themeId={splitBrowseTemplateId}
            onSelect={setSplitBrowseTemplateId}
            extra={
              splitBrowseHasOverride ? (
                <Button size="small" variant="text" sx={{ mt: 1 }} onClick={clearSplitBrowseTemplateOverride}>
                  Follow card palette
                </Button>
              ) : null
            }
          />
          <SelectorSection
            icon={<ViewColumnIcon fontSize="small" />}
            title="Split — right (preview)"
            subtitle="Detail pane"
            themeId={splitPreviewTemplateId}
            onSelect={setSplitPreviewTemplateId}
            extra={
              splitPreviewHasOverride ? (
                <Button size="small" variant="text" sx={{ mt: 1 }} onClick={clearSplitPreviewTemplateOverride}>
                  Follow card palette
                </Button>
              ) : null
            }
          />
        </>
      ) : null}

      <Divider sx={{ my: 1 }} />
      <Button
        size="small"
        variant="text"
        onClick={() => {
          clearSplitBrowseTemplateOverride();
          clearSplitPreviewTemplateOverride();
        }}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        Both split panes follow cards
      </Button>
    </SharedTemplateChooser>
  );
}
